import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Award, MapPin, Phone, Mail, Globe, Star, Clock, Tag,
  Calendar, ArrowRight, CheckCircle, ExternalLink, Users, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXTERNAL_DIRECTORY_URL = process.env.REACT_APP_EXTERNAL_DIRECTORY_URL || 'https://search.aihorsepowered.com';

const BADGE_IMAGE_URL = "https://customer-assets.emergentagent.com/job_daa57298-04ae-460b-8f69-e638243862e1/artifacts/svw0chlf_website%20badge.png";

export default function BadgeLandingPage() {
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [businessId]);

  const loadData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/badge/landing/${businessId}`);
      setData(response.data);
    } catch (err) {
      console.error('Error loading badge data:', err);
      setError('Business not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <Award className="w-16 h-16 text-amber-500 mx-auto mb-4 opacity-50" />
          <h1 className="text-2xl font-bold text-white mb-2">Business Not Found</h1>
          <p className="text-gray-400 mb-6">This Best of Best listing could not be found.</p>
          <a href={EXTERNAL_DIRECTORY_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-amber-500 hover:bg-amber-600 text-black">
              Browse Directory
            </Button>
          </a>
        </div>
      </div>
    );
  }

  const { featured_business, nearby_businesses, local_deals, local_events } = data;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      {/* Hero Section with Featured Business */}
      <div className="relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(251,191,36,0.1)_0%,transparent_50%)]" />
        
        <div className="max-w-6xl mx-auto px-4 py-12 relative z-10">
          {/* Badge and Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <img 
              src={BADGE_IMAGE_URL} 
              alt="Voted Best of Best Badge" 
              className="w-40 h-auto mx-auto mb-6"
            />
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Voted Best of Best
            </h1>
            <p className="text-amber-400 text-lg">Local Excellence, AI-Certified</p>
          </motion.div>

          {/* Featured Business Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-amber-900/30 via-gray-800/50 to-gray-900/80 rounded-3xl border-2 border-amber-500/40 p-6 md:p-8 mb-12"
          >
            <div className="flex flex-col md:flex-row gap-6">
              {/* Business Logo/Image */}
              <div className="shrink-0">
                {featured_business.logo_url ? (
                  <img 
                    src={featured_business.logo_url} 
                    alt={featured_business.name}
                    className="w-32 h-32 rounded-2xl object-cover border-2 border-amber-500/30"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                    <Building2 className="w-16 h-16 text-white" />
                  </div>
                )}
              </div>

              {/* Business Details */}
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl md:text-3xl font-bold text-white">
                        {featured_business.name}
                      </h2>
                      {featured_business.is_ai_certified && (
                        <span className="px-3 py-1 bg-amber-500/20 border border-amber-500/40 rounded-full text-amber-400 text-xs font-semibold flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          AI Certified
                        </span>
                      )}
                    </div>
                    <p className="text-gray-300 text-sm mb-3">{featured_business.category}</p>
                    <p className="text-gray-400 leading-relaxed">{featured_business.description}</p>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  {featured_business.address && (
                    <div className="flex items-center gap-2 text-gray-300 text-sm">
                      <MapPin className="w-4 h-4 text-amber-400" />
                      <span>{featured_business.address}, {featured_business.city}, {featured_business.state} {featured_business.zip_code}</span>
                    </div>
                  )}
                  {featured_business.phone && (
                    <div className="flex items-center gap-2 text-gray-300 text-sm">
                      <Phone className="w-4 h-4 text-amber-400" />
                      <a href={`tel:${featured_business.phone}`} className="hover:text-amber-400">{featured_business.phone}</a>
                    </div>
                  )}
                  {featured_business.email && (
                    <div className="flex items-center gap-2 text-gray-300 text-sm">
                      <Mail className="w-4 h-4 text-amber-400" />
                      <a href={`mailto:${featured_business.email}`} className="hover:text-amber-400">{featured_business.email}</a>
                    </div>
                  )}
                  {featured_business.website && (
                    <div className="flex items-center gap-2 text-gray-300 text-sm">
                      <Globe className="w-4 h-4 text-amber-400" />
                      <a href={featured_business.website} target="_blank" rel="noopener noreferrer" className="hover:text-amber-400">
                        Visit Website <ExternalLink className="w-3 h-3 inline" />
                      </a>
                    </div>
                  )}
                </div>

                {/* Rating & Actions */}
                <div className="flex flex-wrap items-center gap-4">
                  {featured_business.rating && (
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-5 h-5 ${i < Math.floor(featured_business.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} 
                        />
                      ))}
                      <span className="text-white ml-2 font-semibold">{featured_business.rating}</span>
                    </div>
                  )}
                  <Link to={`/directory/business/${featured_business.id}`}>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                      View Full Profile <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Nearby Best of Best Section */}
          {nearby_businesses && nearby_businesses.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-6 h-6 text-amber-400" />
                <h3 className="text-xl font-bold text-white">More Best of Best Near You</h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {nearby_businesses.map((business, index) => (
                  <Link 
                    key={business.id} 
                    to={`/directory/business/${business.id}`}
                    className="block"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-4 hover:border-amber-500/30 transition-all group"
                    >
                      <div className="flex items-start gap-3">
                        {business.logo_url ? (
                          <img 
                            src={business.logo_url} 
                            alt={business.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-amber-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-white group-hover:text-amber-400 transition-colors truncate">
                            {business.name}
                          </h4>
                          <p className="text-sm text-gray-400 truncate">{business.category}</p>
                          {business.city && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {business.city}, {business.state}
                            </p>
                          )}
                        </div>
                        {business.is_ai_certified && (
                          <CheckCircle className="w-5 h-5 text-amber-400 shrink-0" />
                        )}
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </div>
            </motion.section>
          )}

          {/* Local Deals Section */}
          {local_deals && local_deals.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-6">
                <Tag className="w-6 h-6 text-green-400" />
                <h3 className="text-xl font-bold text-white">Local Deals & Coupons</h3>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {local_deals.map((deal, index) => (
                  <motion.div
                    key={deal.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="bg-gradient-to-br from-green-900/30 to-gray-800/50 rounded-xl border border-green-500/20 p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-green-400 font-semibold">DEAL</span>
                    </div>
                    <h4 className="font-semibold text-white mb-1">{deal.title}</h4>
                    <p className="text-sm text-gray-400 line-clamp-2">{deal.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Local Events Section */}
          {local_events && local_events.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-6 h-6 text-purple-400" />
                <h3 className="text-xl font-bold text-white">Local Events</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {local_events.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="bg-gradient-to-br from-purple-900/30 to-gray-800/50 rounded-xl border border-purple-500/20 p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-purple-400" />
                      <span className="text-xs text-purple-400 font-semibold">
                        {event.event_date}
                      </span>
                    </div>
                    <h4 className="font-semibold text-white mb-1">{event.title}</h4>
                    <p className="text-sm text-gray-400 line-clamp-2">{event.description}</p>
                    {event.location && (
                      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.location}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center bg-gradient-to-r from-amber-900/20 via-gray-800/50 to-amber-900/20 rounded-2xl border border-amber-500/20 p-8"
          >
            <Award className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Join the Best of Best Network</h3>
            <p className="text-gray-400 mb-6 max-w-xl mx-auto">
              Get AI-certified, connect with local businesses, and showcase your excellence to the community.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={EXTERNAL_DIRECTORY_URL} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                  Browse Directory
                </Button>
              </a>
              <Link to="/register">
                <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                  Get Your Badge <Award className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-800 py-6 text-center">
        <p className="text-gray-500 text-sm">
          Part of the <span className="text-amber-400">Best of Best</span> AI-Powered Business Directory
        </p>
      </div>
    </div>
  );
}
